import GCInstant, { analytics } from '@play-co/gcinstant';
import StateObserver from 'src/StateObserver';
import { FullSequenceId } from '../ActionSequence';
import { ProductId } from 'src/replicant/iap';
import { updateRevenueSequence } from '../analytics';
import {
  incrementCancelled,
  resetCancelledExact,
  resetCancelledSession,
  updateSessionPurchaseAnalytics,
} from '../../state/analytics';
import { serverSyncedLocalTime } from '../utils';
import { NativePromoOrigin } from 'src/replicant/native';

/**
 * Track a Google Tag Manager event.
 *
 * The GTM script is injected into the outer HTML document containing the game iframe (see .cdn-config.json).
 */
export function eventGTM(eventName: string, props?: Record<string, any>): void {
  // Cancel if is not running on platform web or if dataLayer is not defined
  if (GCInstant.platformID !== 'web' || !window.dataLayer) {
    return;
  }

  console.warn('>>> sending GTM event:', eventName, 'to window', window.parent);

  window.top.postMessage(eventName, '*');
  window.dataLayer.push({ event: eventName, ...props });
}

////////////////////////////////////////////////////////////////////////////////
// Keys
////////////////////////////////////////////////////////////////////////////////
let keyIdx = 0;

export function keyGen(): string {
  keyIdx++;

  return [
    GCInstant.playerID,
    StateObserver.getSessionData().sessionId,
    StateObserver.now(),
    keyIdx,
    Math.random().toString(36).slice(2, 10),
  ].join('-');
}

////////////////////////////////////////////////////////////////////////////////
// Dialog open/close events
////////////////////////////////////////////////////////////////////////////////
const DIALOG_BLACKLIST = ['popupAction', 'popupResult', 'popupTutorial'];

export function trackDialogOpen(props: { id: string }) {
  if (DIALOG_BLACKLIST.includes(props.id)) return;

  analytics.pushEvent('DialogOpen', props);
}

export function trackDialogClose(props: { id: string }) {
  if (DIALOG_BLACKLIST.includes(props.id)) return;

  analytics.pushEvent('DialogClose', props);
}

////////////////////////////////////////////////////////////////////////////////
// PayloadFailure event
////////////////////////////////////////////////////////////////////////////////
type PayloadFailurePayload = {
  key: string;
  channel: string;
  feature: string;
  subFeature: string;
  playerID: string;
};

export function trackPayloadFailure(
  error: Error,
  props: PayloadFailurePayload & {
    daysElapsed: number;
    type: 'kvStoreFetchFailed' | 'notFound' | 'parseFailed';
  },
) {
  analytics.pushError('PayloadFailure', error, props);
}

export function trackPayloadWriteFailure(
  error: Error,
  props: PayloadFailurePayload,
) {
  analytics.pushError('PayloadWriteFailure', error, props);
}

////////////////////////////////////////////////////////////////////////////////
// Performance event
////////////////////////////////////////////////////////////////////////////////
export function trackPerformance(opts: {
  fps: number;
  choppiness: number;

  jsHeapSizeLimit: number | null;
  jsHeapSizeTotal: number | null;
  jsHeapSizeUsed: number | null;

  dispatchMaxTime: number;
  dispatchAverageTime: number;
  dispatchMaxListeners: number;
  dispatchAverageListeners: number;

  // Listed in estimated order of occurrence
  timeToAppConstructor: number;
  timeToInitializeAsync: number;
  timeToReplicantLogin: number;
  timeToEarlyLoadTournament: number;
  timeToBlockingAssets: number;
  timeToStartGameAsync: number;
  timeToEntryPreChecks: number;
  timeToEntryFinalEvent: number;
  timeToGameVisible: number;
  timeToPaymentsReady: number;
  timeToFriendsStates: number;
  timeToInferTournamentPayloadData: number;

  blockingAssetsSize: number;
  blockingAssetsCount: number;
  blockingAssetsCountOk: number;
}) {
  analytics.pushEvent('Performance', opts);
}

////////////////////////////////////////////////////////////////////////////////
// ConfirmToS_Dialog_view
////////////////////////////////////////////////////////////////////////////////
export function trackToSView() {
  analytics.pushEvent('ConfirmToS_Dialog_view', {});
}

export function trackToSDetails({ mode }: { mode: 'pp' | 'tos' }) {
  analytics.pushEvent('ConfirmToS_Details_view', { mode });
}

export function trackToSConfirm() {
  analytics.pushEvent('ConfirmToS_Button_click', {});
}

////////////////////////////////////////////////////////////////////////////////
// SequenceFinish
////////////////////////////////////////////////////////////////////////////////
export function trackSequenceFinish(id: FullSequenceId) {
  analytics.pushEvent('SequenceFinish', { id });
}

////////////////////////////////////////////////////////////////////////////////
// SequenceStart
////////////////////////////////////////////////////////////////////////////////
export function trackSequenceStart(id: FullSequenceId) {
  analytics.pushEvent('SequenceStart', { id });
}

////////////////////////////////////////////////////////////////////////////////
// DebugPaymentsCatalog
////////////////////////////////////////////////////////////////////////////////
export function trackDebugPaymentsCatalog(props: {
  [productId: string]: string;
}) {
  analytics.pushEvent('DebugPaymentsCatalog', props);
}

////////////////////////////////////////////////////////////////////////////////
// Revenue event
////////////////////////////////////////////////////////////////////////////////
const REVENUE_IAP_NET = 0.7; // Revenue IAP net multiplier

const PURCHASE_ANALYTICS_SCHEMA_VERSION = '2022-08-12';

const purchaseActionsMap = {
  tapped: 'PurchaseTapped',
  failure: 'PurchaseFailure',
  success: 'PurchaseSuccess',
};

export function trackRevenue(opts: {
  revenueType: 'in_app_purchase';
  revenueGross: number;
  revenueGrossLocal: number;
  currencyCodeLocal: string;
  dollarToLocalRate: number;
  productID: ProductId;
  feature: string;
  subFeature: string;
}) {
  const absoluteIapCount = StateObserver.replicant.getPurchaseHistory().length;
  const sequenceOpts = {
    absoluteIapSequence: absoluteIapCount,
  };

  analytics.pushRevenue({
    ...opts,
    ...sequenceOpts,
  });

  // Update revenue sequence analytics on redux
  updateRevenueSequence(opts.revenueType, opts.feature);
}

function trackPurchaseBase<T extends { feature: string; revenueGross: number }>(
  purchaseAction: 'tapped' | 'failure' | 'success',
  opts: T,
) {
  let state = StateObserver.getState();
  const purchaseTime = state.analytics.purchaseTime;

  const updateOpts = {
    purchaseAction,
    purchaseTime,
    feature: opts.feature,
  };

  // StateObserver.invoke.updatePurchaseAnalytics(updateOpts);
  StateObserver.dispatch(updateSessionPurchaseAnalytics(updateOpts));

  state = StateObserver.getState();

  // const actionAnalytics = state.user.analytics.purchases[purchaseAction];
  const actionSessionAnalytics = state.analytics.purchases[purchaseAction];
  // const schemaVersion = state.user.analytics.purchases.schemaVersion;

  let revenueType = 'facebookPayments';
  if (GCInstant.osType === 'ANDROID_APP') {
    revenueType = 'androidIAP';
  } else if (GCInstant.osType === 'IOS_APP') {
    revenueType = 'iosIAP';
  }

  const eventOpts = {
    revenueNet: opts.revenueGross * REVENUE_IAP_NET,
    revenueType,
    breadcrumb: state.ui.stack,
    // firstEntrySequenceSchema: schemaVersion === '' ? null : schemaVersion,
    // sequenceAbsolute: actionAnalytics.absolute,
    sequenceSession: actionSessionAnalytics.sequence,
    // sequenceToday: getTodayCounter(purchaseTime, actionAnalytics.today),
    // sequenceFeatureAbsolute:
    //   actionAnalytics.absoluteByFeature[opts.feature] || 0,
    sequenceFeatureSession:
      actionSessionAnalytics.sequenceByFeature[opts.feature] || 0,
    // sequenceFeatureToday: getTodayCounter(
    //   purchaseTime,
    //   actionAnalytics.todayByFeature[opts.feature],
    // ),
  };

  analytics.pushEvent(purchaseActionsMap[purchaseAction], {
    ...opts,
    ...eventOpts,
  });
}

export function trackPurchaseTapped(opts: {
  revenueGross: number;
  productID: ProductId;
  feature: string;
  subFeature: string;
}) {
  const state = StateObserver.getState();
  const elapsedSeconds =
    (Date.now() - state.analytics.purchaseShownTime) / 1000;

  trackPurchaseBase('tapped', {
    ...opts,
    elapsedSeconds,
  });
}

export function trackPurchaseFailure(opts: {
  revenueGross: number;
  productID: ProductId;
  feature: string;
  subFeature: string;
  elapsedSeconds: number;
  userCancelled: boolean;
  errorMessage: string;
  errorCode: string;
}) {
  trackPurchaseBase('failure', opts);

  const state = StateObserver.getState();
  // const purchaseAnalytics = state.user.analytics.purchases;

  const screenKey = state.ui.currentPopup;

  // Increment all applicable cancelled analytics
  if (opts.userCancelled) {
    // StateObserver.invoke.updateCancelledAnalytics({
    //   featureKey: opts.feature,
    //   timeNow: serverSyncedLocalTime(),
    // });

    const exactKey = opts.productID + screenKey;
    StateObserver.dispatch(incrementCancelled({ screenKey, exactKey }));
  }
}

export function trackPurchaseSuccess(opts: {
  revenueGross: number;
  productID: ProductId;
  feature: string;
  subFeature: string;
  elapsedSeconds: number;
  beforeFreeGems: number;
  beforePaidGems: number;
  afterFreeGems: number;
  afterPaidGems: number;
}) {
  const state = StateObserver.getState();
  // const purchaseAnalytics = state.user.analytics.purchases;
  const sessionAnalytics = state.analytics;

  const featureKey = opts.feature;
  const screenKey = state.ui.currentPopup;
  const exactKey = opts.productID + screenKey;
  const timeNow = serverSyncedLocalTime();

  trackPurchaseBase('success', {
    isFirst: StateObserver.replicant.getPurchaseHistory().length <= 1,
    sequenceCancelledExact:
      sessionAnalytics.sequenceCancelledExact[exactKey] || 0,
    // sequenceCancelledFeature:
    //   purchaseAnalytics.cancelledFeature[featureKey] || 0,
    sequenceCancelledScreen:
      sessionAnalytics.sequenceCancelledScreen[screenKey] || 0,
    sequenceCancelledSession: sessionAnalytics.sequenceCancelledSession,
    // sequenceCancelledToday: getTodayCounter(
    //   timeNow,
    //   purchaseAnalytics.cancelledToday,
    // ),
    ...opts,
  });

  // Reset all applicable sequenceCancelled counters
  // StateObserver.invoke.resetCancelledAnalytics({
  //   featureKey,
  //   timeNow,
  // });

  StateObserver.dispatch(resetCancelledExact());
  StateObserver.dispatch(resetCancelledSession());
}

////////////////////////////////////////////////////////////////////////////////
// Native
////////////////////////////////////////////////////////////////////////////////
export function trackNativeDialogView(
  deviceType: 'ios' | 'android' | 'unknown',
) {
  analytics.pushEvent('GoToStore_Dialog_view', { device: deviceType });
}

export function trackNativeDialogButton(
  deviceType: 'ios' | 'android' | 'unknown',
) {
  analytics.pushEvent('GoToStore_Button_click', { device: deviceType });
}

export function trackAppPromoPopupOpen(feature: NativePromoOrigin) {
  analytics.pushEvent('AppPromoPopupOpen', { feature });
}

export function trackCrossplayPopupOpen(feature: NativePromoOrigin) {
  analytics.pushEvent('CrossplayPopupOpen', { feature });
}

////////////////////////////////////////////////////////////////////////////////
// This event fires when currency exchange has finished
////////////////////////////////////////////////////////////////////////////////
export function trackCurrencyExchangeFinish(opts: {
  earnedType: 'b_point' | 'energy';
  paidType: 'gems';
  earned: number;
  paid: number;
}) {
  analytics.pushEvent('CurrencyExchangeFinish', {
    // Type of currency earned
    currencyEarned: opts.earnedType,
    // Type of currency paid
    currencyPaid: opts.paidType,
    // Total amount of the currency earned
    amountEarned: opts.earned,
    // Total amount of the currency paid
    amountPaid: opts.paid,
  });
}

////////////////////////////////////////////////////////////////////////////////
// This event fires when the user updates their profile
////////////////////////////////////////////////////////////////////////////////
export function trackProfileUpdate({
  imageId,
  isLineName,
  imageUpdated,
  nameUpdated,
}: {
  imageId: string;
  isLineName: boolean;
  imageUpdated: boolean;
  nameUpdated: boolean;
}) {
  analytics.pushEvent('InGameProfileUpdate', {
    imageId,
    isLineName,
    imageUpdated,
    nameUpdated,
  });
}

////////////////////////////////////////////////////////////////////////////////
// The pet has ascended
////////////////////////////////////////////////////////////////////////////////
export function trackPetDie() {
  analytics.pushEvent('PetDie');
}

////////////////////////////////////////////////////////////////////////////////
// A new pet has spawned
////////////////////////////////////////////////////////////////////////////////
export function trackPetSpawn({
  feature, // The feature that triggered the spawn
  subFeature,
}:
  | {
      feature: 'new_game';
      subFeature: 'new_game'; // For new_game feature
    }
  | {
      feature: 'death';
      subFeature: // For death feature
      'death_baby' | 'death_kid' | 'death_youth' | 'death_adult';
    }
  | {
      feature: 'debut';
      subFeature: 'debut_inviter' | 'debut_invitee';
    }) {
  analytics.pushEvent('PetSpawn', {
    feature,
    $subFeature: subFeature,
  });
}
